import { Accordion, AccordionDetails, AccordionSummary, Button, Card, CardContent, Dialog, DialogActions, DialogContent, DialogContentText, FormControlLabel, Grid, IconButton, Switch, Typography } from "@material-ui/core";
import { Delete, ExpandMore } from "@material-ui/icons";
import { FieldArray, useFormikContext } from "formik";
import { FC, useState } from "react";
import AlertMessage from "src/components/ui-components/AlertMessage";
import InputField from "src/components/ui-components/InputField";

type WidgetFilterValue = {
  Tag: string | null;
  Value: string | null;
  SortOrder: number | null;
  IsDefault: boolean | null;
  IsEnabled: boolean | null;
};

type FormValues = {
  Filter: {
    FilterName: string | null;
    DisplayName: string | null;
    GroupByValue: string | null;
    Values: WidgetFilterValue[];
  };
};

const defaultWidgetFilterValue = {
  Tag: null,
  Value: null,
  SortOrder: 1,
  IsDefault: false,
  IsEnabled: true,
};

type WidgetFilterCardProps = {
  formRef: any;
  setAreDeleteChanges: any;
};

const WidgetFilterCard: FC<WidgetFilterCardProps> = ({ formRef, setAreDeleteChanges }) => {
  const { values, setFieldValue, setFieldTouched } = useFormikContext<FormValues>();
  const [deleteFilterIndex, setDeleteFilterIndex] = useState<number | null>(null);
  

  const truncateText = (text, maxLength) => {
    if (text.length > maxLength) {
      return text.substring(0, maxLength) + "...";
    }
    return text;
  };

  const handleFilterValueDeleteConfirm = () => {
    let updatedFilterValues = [...values.Filter.Values];
    if (updatedFilterValues[deleteFilterIndex].IsDefault && updatedFilterValues.filter((x, index) => x.IsEnabled && index !== deleteFilterIndex).length > 0) {
      const firstEnabledIndex = updatedFilterValues.findIndex((x, index) => x.IsEnabled && index !== deleteFilterIndex);
      updatedFilterValues[firstEnabledIndex].IsDefault = true;
    }
    const deletedSortOrder = updatedFilterValues[deleteFilterIndex].SortOrder;
    updatedFilterValues.splice(deleteFilterIndex, 1);
    updatedFilterValues.forEach(x => {
      if (x.SortOrder > deletedSortOrder) {
        x.SortOrder = x.SortOrder - 1;
      }
    });
    setFieldValue('Filter.Values', updatedFilterValues);
    setFieldTouched('Filter.Values', false);
    setDeleteFilterIndex(null);
    setAreDeleteChanges(true);
  };

  const handleFilterValueDeleteInitialize = (idx) => {
    setDeleteFilterIndex(idx);
  };

  const handleToggleIsDefault = (idx) => {
    const widgetFilterValues = [...values.Filter.Values];
    const defaultCount = widgetFilterValues.filter(value => value.IsDefault).length;

    if (defaultCount === 1 && widgetFilterValues[idx].IsDefault) {
      AlertMessage({ type: 'warning', msg: 'Filter must have a default value' });
      return;
    }

    const updatedWidgetFilterValues = widgetFilterValues.map((value, index) => {
      if (index === idx) {
        value.IsDefault = !value.IsDefault;
        if (!value.IsEnabled) {
          value.IsEnabled = true;
        }
      } else {
        value.IsDefault = false;
      }
      return value;
    });

    if (updatedWidgetFilterValues.filter(value => value.IsDefault).length === 0) {
      AlertMessage({ type: 'warning', msg: 'Filter must have a default value' });
      setFieldValue('Filter.Values', widgetFilterValues);
    } else {
      setFieldValue('Filter.Values', updatedWidgetFilterValues);
    }
  };

  const handleToggleIsEnabled = (event, idx) => {
    let widgetFilterValues = [...values.Filter.Values];
    if (event.target.checked) {
      widgetFilterValues[idx].IsEnabled = true;
    } else {
      if (widgetFilterValues[idx].IsDefault) {
        const firstEnabledIndex = widgetFilterValues.findIndex((x, index) => x.IsEnabled && index !== idx);
        if (firstEnabledIndex === -1) {
          AlertMessage({ type: 'warning', msg: 'Cannot disable last enabled value' });
          return;
        } else {
          widgetFilterValues[firstEnabledIndex].IsDefault = true;
          widgetFilterValues[idx].IsDefault = false;
          widgetFilterValues[idx].IsEnabled = false;
        }
      } else {
        widgetFilterValues[idx].IsEnabled = false;
      }
    }
    setFieldValue('Filter.Values', widgetFilterValues);
  };

  const AddWidgetFilterValue = () => {
    const newWidgetFilterValue = {
      ...defaultWidgetFilterValue,
      IsDefault: values.Filter.Values.length === 0 ? true : false,
      SortOrder: (formRef?.current?.values?.Filter?.Values?.length + 1),
    };
    formRef?.current?.setFieldValue(`Filter.Values`, [ ...formRef?.current?.values?.Filter?.Values, newWidgetFilterValue ]);
  };

  return (
    <>
      <Card>
        <CardContent>
          <Accordion
            defaultExpanded
            key='widget-card-accordion'
            style={{ overflowY: "auto", maxHeight: "350px" }}
          >
            <AccordionSummary
              expandIcon={<ExpandMore />}
              aria-controls={`WidgetFilter-content`}
              id={`WidgetFilter`}
              sx={{ alignItems: "center" }}
            >
              <Typography
                sx={{
                  fontSize: "20px !important",
                  fontWeight: "bold !important",
                }}
              >
                Widget Filter
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={12} md={4} lg={4} xl={4}>
                  <InputField
                    name={`Filter.FilterName`}
                    label="Filter Name"
                  />
                </Grid>
                <Grid item xs={12} sm={12} md={4} lg={4} xl={4}>
                  <InputField
                    name={`Filter.DisplayName`}
                    label="Display Name"
                  />
                </Grid>
                <Grid item xs={12} sm={12} md={4} lg={4} xl={4}>
                  <InputField
                    name={`Filter.GroupByValue`}
                    label="Group By Value"
                  />
                </Grid>
                <Grid item xs={12} sm={12} md={12} lg={12} xl={12} display="flex" justifyContent="right">
                  <Button sx={{ background: "linear-gradient(to bottom, #072f40, #072f40)", minWidth: 100, color: "#fff" }} onClick={AddWidgetFilterValue}>
                    Add Value
                  </Button>
                </Grid>
                <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                  <FieldArray name={`Filter.Values`}>
                  {() => (
                    values.Filter.Values.map((filterValue, idx) => (
                      <div key={idx} style={{ marginBottom: "30px" }}>
                        <Typography
                          sx={{
                            marginBottom: '2%',
                            color: "#000",
                            textAlign: "left",
                            backgroundColor: "transparent",
                            fontSize: "16px!important",
                            display: "block",
                            width: "20%",
                            "&:hover": {
                              backgroundColor: "transparent",
                              outline: "none",
                            },
                            "&:focus": {
                              outline: "none",
                            },
                          }}
                        >
                          {filterValue.Tag ? filterValue.Tag.length > 20 ? truncateText(filterValue.Tag, 20) : filterValue.Tag : 'Filter Value'}
                        </Typography>
                        <Grid container spacing={3}>
                          <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                            <InputField
                              label="Filter Value Tag"
                              name={`Filter.Values.${idx}.Tag`}
                              InputLabelProps={{
                                shrink: !!values.Filter.Values[idx].Tag,
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                            <InputField
                              label="Filter Value"
                              name={`Filter.Values.${idx}.Value`}
                              InputLabelProps={{
                                shrink: !!values.Filter.Values[idx].Value,
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={12} md={3} lg={3} xl={3}>
                            <InputField
                              label="Sort Order"
                              name={`Filter.Values.${idx}.SortOrder`}
                              InputLabelProps={{
                                shrink: !!values.Filter.Values[idx].SortOrder,
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={12} md={3} lg={3} xl={3}>
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={values.Filter.Values[idx].IsDefault === true}
                                  onChange={(event) => handleToggleIsDefault(idx)}
                                />
                              }
                              label="Is Default"
                              labelPlacement='start'
                            />
                          </Grid>
                          <Grid item xs={12} sm={12} md={3} lg={3} xl={3}>
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={values.Filter.Values[idx].IsEnabled === true}
                                  onChange={(event) => handleToggleIsEnabled(event, idx)}
                                />
                              }
                              label="Is Enabled"
                              labelPlacement='start'
                            />
                          </Grid>
                          <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
                            <IconButton
                              onClick={() => handleFilterValueDeleteInitialize(idx)}
                            >
                              <Delete />
                            </IconButton>
                          </Grid>
                        </Grid>
                      </div>
                    ))
                  )}
                  </FieldArray>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        </CardContent>
      </Card>
      <Dialog
        open={deleteFilterIndex !== null}
        onClose={(event, reason) => {
          if (reason !== "backdropClick") {
            setDeleteFilterIndex(null);
          }
        }}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            sx={{
              background:
                "linear-gradient(to bottom, #939ca3, #939ca3)",
              minWidth: 100,
            }}
            variant="contained"
            onClick={() => setDeleteFilterIndex(null)}
          >
            No
          </Button>
          <Button
            sx={{
              background:
                "linear-gradient(to bottom, #072f40, #072f40)",
              minWidth: 100,
            }}
            variant="contained"
            onClick={handleFilterValueDeleteConfirm}
            autoFocus
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
};

export default WidgetFilterCard;